import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { InvestmentTransaction, InvestmentTransactionData } from '@/types/investments';

/**
 * Hook to fetch all transactions for a specific investment account
 * Transactions are sorted by date in descending order (most recent first)
 * 
 * @param investmentId - The ID of the investment account
 * @returns Query result containing transactions for the investment account
 * 
 * @example
 * const { data: transactions, isLoading } = useInvestmentTransactions('inv-123');
 */
export function useInvestmentTransactions(investmentId: string) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['investment-transactions', investmentId, user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('investment_transactions')
        .select('*')
        .eq('investment_account_id', investmentId)
        .order('transaction_date', { ascending: false });
      
      if (error) throw error;
      return data as InvestmentTransaction[];
    },
    enabled: !!user && !!investmentId,
  });
}

/**
 * Hook to create a new investment transaction (deposit or withdrawal)
 * Uses the create_investment_transaction RPC function to handle balance updates
 * and linked transaction creation
 * 
 * @returns Mutation function to create investment transaction
 * 
 * @example
 * const createTransaction = useCreateInvestmentTransaction();
 * await createTransaction.mutateAsync({
 *   transaction_type: 'deposit',
 *   investment_account_id: 'inv-123',
 *   amount: 1000,
 *   currency: 'USD',
 *   source_account_id: 'acc-456',
 *   transaction_date: '2024-01-15',
 * });
 */
export function useCreateInvestmentTransaction() {
  const qc = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async (transaction: InvestmentTransactionData) => {
      if (!user) throw new Error('User not authenticated');

      const isInterest = transaction.transaction_type === 'interest';

      let accountId: string | null = null;
      let conversionRate = 1;

      if (!isInterest) {
        // Determine which account ID to use based on transaction type
        accountId = transaction.transaction_type === 'deposit'
          ? transaction.source_account_id
          : transaction.destination_account_id;

        if (!accountId) {
          throw new Error(`${transaction.transaction_type === 'deposit' ? 'Source' : 'Destination'} account is required`);
        }

        // Fetch the account to get its currency for conversion rate calculation
        const { data: account, error: accountError } = await supabase
          .from('accounts')
          .select('currency')
          .eq('id', accountId)
          .single();

        if (accountError) throw accountError;

        // Calculate conversion rate (default to 1 if same currency)
        if (account.currency !== transaction.currency) {
          // Fetch conversion rate from exchange_rates table
          const { data: rateData, error: rateError } = await supabase
            .from('exchange_rates')
            .select('rate')
            .eq('from_currency', transaction.currency)
            .eq('to_currency', account.currency)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (rateError) throw rateError;
          conversionRate = rateData?.rate || 1;
        }
      }

      // Extract deposit_fee from transaction data (new field, defaults to 0)
      const depositFee = transaction.transaction_type === 'deposit' 
        ? (transaction.deposit_fee || 0) 
        : 0;

      // Call the RPC function to create the transaction
      const { data, error } = await supabase.rpc('create_investment_transaction', {
        p_user_id: user.id,
        p_investment_account_id: transaction.investment_account_id,
        p_account_id: accountId,
        p_transaction_type: transaction.transaction_type,
        p_amount: transaction.amount,
        p_currency: transaction.currency,
        p_fee: transaction.withdrawal_fee || 0,
        p_transaction_date: transaction.transaction_date,
        p_description: transaction.description || '',
        p_notes: transaction.notes || '',
        p_conversion_rate: conversionRate,
        p_deposit_fee: depositFee,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate relevant queries to refresh data
      qc.invalidateQueries({ queryKey: ['investment-account', variables.investment_account_id] });
      qc.invalidateQueries({ queryKey: ['investment-transactions', variables.investment_account_id] });
      qc.invalidateQueries({ queryKey: ['investments'] });
      qc.invalidateQueries({ queryKey: ['pension-plans'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}
